'use client';

import { memo, useCallback } from 'react';

import { type Board } from '@air/api/types';

import { SubBoardsTree } from '~/components/tree/SubBoardsTree';
import { TreeItem, type TreeItemProps } from '~/components/tree/TreeItem';
import { useTreeStateContext } from '~/contexts/TreeStateContext';

export type BoardsTreeItemProps = Pick<TreeItemProps, 'selected'> & {
  board: Board;
  depth: number;
  workspaceId: string;
  onClick: (board: Board) => void;
  onMouseEnter?: (board: Board) => void;
  isDisabled?: (board: Board) => boolean;
  selectedBoardId?: string;
};

export const BoardsTreeItem = memo(
  ({
    board,
    depth = 0,
    workspaceId,
    onClick,
    onMouseEnter,
    selected,
    isDisabled,
    selectedBoardId,
  }: BoardsTreeItemProps) => {
    const { getNodeStateById, onNodeChange } = useTreeStateContext();
    const open = getNodeStateById(board.id);

    const handleOpenChange = useCallback<Required<TreeItemProps>['onOpenChange']>(
      (open) => {
        onNodeChange(board.id, open);
      },
      [board.id, onNodeChange],
    );

    const handleBoardClick = useCallback(() => {
      onClick(board);
    }, [board, onClick]);

    const handleBoardMouseEnter = useCallback(() => {
      onMouseEnter?.(board);
    }, [board, onMouseEnter]);

    return (
      <>
        <TreeItem
          depth={depth}
          disabled={!!isDisabled?.(board)}
          onClick={handleBoardClick}
          onMouseEnter={handleBoardMouseEnter}
          onOpenChange={handleOpenChange}
          open={open}
          selected={selected || selectedBoardId === board.id}
        >
          {board.title}
        </TreeItem>

        {open && (
          <SubBoardsTree
            boardId={board.id}
            depth={depth + 1}
            isDisabled={isDisabled}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            selectedBoardId={selectedBoardId}
            workspaceId={workspaceId}
          />
        )}
      </>
    );
  },
);

BoardsTreeItem.displayName = 'BoardsTreeItem';
